import React, { useEffect } from 'react';

import { arrowLeft } from '@/assets/icons';
import { getZonedStartDateTimeISO } from '@/components/Layout/components/CreateSetlistPanel';
import { generatePositions } from '@/components/Layout/components/GenerateSetlist';
import { RegenenerateButton } from '@/components/Layout/components/GenerateSetlist/components/ActionContent/ActionContent';
import AddPassageBottomCard from '@/components/Layout/components/GenerateSetlist/components/AddPassageBottomCard/AddPassageBottomCard';
import AddPassageForm from '@/components/Layout/components/GenerateSetlist/components/AddPassageForm/AddPassageForm';
import { PassageChipPanel } from '@/components/Layout/components/GenerateSetlist/components/PassageChipPanel/PassageChipPanel';
import SetlistAnalysis from '@/components/Layout/components/GenerateSetlist/components/SetlistAnalysis/SetlistAnalysis';
import { SetlistChipRow } from '@/components/Layout/components/GenerateSetlist/components/SetlistChipRow/SetlistChipRow';
import { SetlistSummary } from '@/components/Layout/components/GenerateSetlist/components/SetlistSummary';
import { SongsChipPanel } from '@/components/Layout/components/GenerateSetlist/components/SongsChipPanel/SongsChipPanel';
import { SongSummary } from '@/components/Layout/components/GenerateSetlist/components/SongSummary';
import { SortableItem } from '@/components/Layout/components/GenerateSetlist/components/SortableItem/SortableItem';
import styles from '@/components/Layout/components/GenerateSetlist/GenerateSetlist.module.scss';
import { SongAnalysis } from '@/components/Layout/components/SongAnalysisPanel/SongAnalysis';
import { BottomActionLayout } from '@/components/shared/BottomActionLayout/BottomActionLayout';
import { AsaphButton } from '@/components/shared/Button';
import { IndexIndicator } from '@/components/shared/IndexIndicator';
import { ModalSectionCard } from '@/components/shared/ModalSectionCard';
import { ModalSectionTitle } from '@/components/shared/ModalSectionTitle';
import { Tabs } from '@/components/shared/Tabs';
import { UserMilestones } from '@/config/constants';
import {
	useCreateSetlistMutation,
	useGenerateSetlistMutation,
	useGetBiblePassageAnalysisMutation,
} from '@/graphql';
import { classNames } from '@/helpers/classNames';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useLayout } from '@/providers/LayoutProvider';
import { useServices } from '@/providers/ServicesProvider';
import { useSetlists } from '@/providers/SetlistsProvider';
import { useTeam } from '@/providers/TeamProvider';
import { useTeamSongs } from '@/providers/TeamSongsProvider';
import { IconButton, SvgIcon } from '@material-ui/core';
import { Day, nextDay } from 'date-fns';

import { OnboardingContext } from './Onboarding';
import onbStyles from './Onboarding.module.scss';

const StepSeven = () => {
	const {
		setStep,
		step,
		shouldAddService,
		generateSettings,
		setGenerateSettings,
	} = React.useContext(OnboardingContext);
	const { openSong } = useLayout();
	const { updateUser } = useAuth();
	const { team } = useTeam();
	const { services } = useServices();
	const { handleError } = useErrorHandler();
	const [selectedSongIds, setSelectedSongIds] = React.useState<string[]>(
		generateSettings.initialSongIds,
	);
	const [selectedTab, setSelectedTab] = React.useState(0);
	const [isAddPassageOpen, setIsAddPassageOpen] = React.useState(false);
	const [isSongsPanelOpen, setIsSongsPanelOpen] = React.useState(false);
	const [isPassagePanelOpen, setIsPassagePanelOpen] = React.useState(false);

	const fireViewTab = useAnalyticsEvent('Onboarding_Step7_View_Tab');
	const fireOpenSongsPanel = useAnalyticsEvent('Onboarding_Step7_Open_Songs_Panel');
	const fireOpenPassagePanel = useAnalyticsEvent('Onboarding_Step7_Open_Passage_Panel');
	const fireOpenAddPassage = useAnalyticsEvent('Onboarding_Step7_Open_Add_Passage');
	const fireRegenerate = useAnalyticsEvent('Onboarding_Step7_Regenerate');
	const fireContinue = useAnalyticsEvent('Onboarding_Step7_Continue');
	const fireSaveSetlist = useAnalyticsEvent('Onboarding_Step7_Save_Setlist');

	const STEP7_TABS = ['Analysis', 'Sermon', 'Reports'];

	const {
		data: biblePassageData,
		isLoading: isBibleVerseLoading,
		mutateAsync: getBibleVerse,
	} = useGetBiblePassageAnalysisMutation();

	const { mutateAsync: createSetlistAsync } = useCreateSetlistMutation();
	const { refetch: refetchSetlists } = useSetlists();

	const {
		mutateAsync: generateSetlistMutation,
		isLoading: isGenerateLoading,
	} = useGenerateSetlistMutation({
		onSuccess: (generatedSetlistData) => {
			setSelectedSongIds(
				generatedSetlistData.generateSetlist.positions
					.filter((position) => !!position)
					.map((position) => position!.id),
			);
		},
	});

	useEffect(() => {
		if (generateSettings.bibleReference) {
			getBibleVerse({
				reference: generateSettings.bibleReference,
			});
		}
	}, [generateSettings.bibleReference, getBibleVerse]);

	const generateSetlist = React.useCallback(() => {
		fireRegenerate();
		const positions = generatePositions({
			totalPositionsNumber: generateSettings.totalSongPositions,
			postSermonSongPosition: generateSettings.postSermonSongPosition,
			bibleReference: generateSettings.bibleReference,
			lockedTeamSongIds: [],
			currentTeamSongIds: selectedSongIds,
		});
		generateSetlistMutation({
			teamId: team.id,
			request: {
				positions,
			},
		});
	}, [generateSetlistMutation, generateSettings, team.id, selectedSongIds, fireRegenerate]);

	const postSermonSongId = generateSettings.postSermonSongPosition
		? selectedSongIds[generateSettings.postSermonSongPosition - 1]
		: undefined;
	const { findTeamSongsById } = useTeamSongs();
	const postSermonSongName = postSermonSongId
		? findTeamSongsById([postSermonSongId])[0]?.name
		: undefined;
	const biblePassageAnalysis = biblePassageData?.getBiblePassageAnalysis;
	const isLoading = isBibleVerseLoading || isGenerateLoading;

	if (!postSermonSongId) {
		handleError({
			error: new Error('No sermon song selected'),
			errorCode: 'NoSermonSongSelected',
			friendlyMessage: 'No sermon song selected',
		});
		setStep(step - 1);
		return null;
	}

	const sortableItems = selectedSongIds.map((sid, sindex) => {
		return (
			<SortableItem
				key={`small-song-card-${sid}`}
				id={sid}
				onClick={(song) => {
					openSong({
						teamSongId: song.id,
						viewOnly: false,
					});
				}}
				isLoading={isLoading}
				onViewAnalysisClick={() => {
					setSelectedTab(1);
				}}
				onAddPassageClick={() => {
					fireOpenAddPassage();
					setSelectedTab(1);
					setIsAddPassageOpen(true);
				}}
				isPostSermon={
					generateSettings.postSermonSongPosition === sindex + 1
				}
				reference={generateSettings.bibleReference}
				isLightEdit={true}
			/>
		);
	});

	return (
		<>
			<BottomActionLayout
				className={classNames([onbStyles['layout'], styles['root']])}
				contentClassName={classNames([
					styles['generate-root'],
					onbStyles['step-seven'],
				])}
				noPadding
				topContent={
					<IndexIndicator
						className={onbStyles['indicator']}
						dotClassName={onbStyles['dot']}
						total={6}
						current={5}
						variant="stretched"
						onIndicatorClick={(i) => setStep(i + 2)}
						hasHover
						conditionalItems={[
							{ index: 3, visible: shouldAddService },
						]}
					/>
				}
				actionContent={
					<div className={styles['action-content']}>
						<IconButton
							// onClick={() => setIsSettingsPanelOpen(true)}
							onClick={() => setStep(step - 1)}
							className={classNames([styles['more-button']])}
						>
							<SvgIcon
								className={styles['icon']}
								component={arrowLeft}
							/>
						</IconButton>

						<RegenenerateButton
							onClick={generateSetlist}
							className={onbStyles['regenerate-button']}
							label="Regenerate"
						/>
						<AsaphButton
							color="blue"
							label="Continue"
							onClick={async () => {
								try {
									fireContinue();

									const service = services[0];
									let startAt = new Date().toISOString();
									if (service) {
										const candidateDate =
											new Date().getDay() === (service.day as number)
												? new Date()
												: nextDay(new Date(), service.day as Day);
										const candidateStartAt = getZonedStartDateTimeISO(
											service.startAt,
											candidateDate.toISOString(),
										);
										startAt =
											new Date(candidateStartAt).getTime() <= Date.now()
												? getZonedStartDateTimeISO(
													service.startAt,
													nextDay(new Date(), service.day as Day).toISOString(),
												  )
												: candidateStartAt;
									}

									await createSetlistAsync({
										input: {
											startAt,
											teamSongIds: selectedSongIds,
											serviceId: service?.id,
											draft: false,
											generateSettings: {
												totalSongPositions: generateSettings.totalSongPositions,
												postSermonSongPosition: generateSettings.postSermonSongPosition,
												bibleReference: generateSettings.bibleReference,
											},
										},
									});

									refetchSetlists();

									await updateUser({
										input: {
											completedMilestones: [
												UserMilestones.PROFILE_SET_UP,
											],
										},
									});

									fireSaveSetlist({
										songCount: selectedSongIds.length,
										hasBibleReference: !!generateSettings.bibleReference,
										postSermonSongPosition: generateSettings.postSermonSongPosition ?? 0,
									});

									setStep(8);
								} catch (error) {
									handleError({
										error: error as Error,
										errorCode: 'OnboardingContinueError',
										friendlyMessage:
											'Failed to save your setlist',
									});
								}
							}}
						/>
					</div>
				}
			>
				<div className={onbStyles['title-header']}>
					<div className={onbStyles['heading']}>
						Your first setlist
					</div>
					<div className={classNames([onbStyles['text'], 'mt-sm'])}>
						See what Asaph can do.
					</div>
				</div>

				<SetlistChipRow
					hideServiceChip
					bibleReference={generateSettings.bibleReference}
					totalSongPositions={generateSettings.totalSongPositions}
					postSermonSongPosition={
						generateSettings.postSermonSongPosition
					}
					onSongsClick={() => {
						fireOpenSongsPanel();
						setIsSongsPanelOpen(true);
					}}
					onPassageClick={() => {
						fireOpenPassagePanel();
						setIsPassagePanelOpen(true);
					}}
				/>

				<div className={classNames([styles['add-songs-area']])}>
					<div className={styles['add-songs-wrapper']}>
						<div className={classNames([styles['song-card-list']])}>
							{sortableItems}
						</div>
					</div>
				</div>

				<Tabs
					stickyBar
					className={onbStyles['tabs']}
					stickyBarClassName={onbStyles['tabs-sticky-bar']}
					maskClassName={onbStyles['tabs-mask']}
					contentClassName={onbStyles['tabs-content']}
					selectedIndex={selectedTab}
					onChange={(index) => {
						setSelectedTab(index);
						fireViewTab({ tab: STEP7_TABS[index] ?? String(index) });
					}}
					items={[
						{
							title: 'Analysis',
						},
						{
							title: 'Sermon',
						},
						{
							title: 'Reports',
						},
					]}
					renderContent={({ selectedIndex }) => {
						if (selectedIndex === 0) {
							return (
								<ModalSectionCard
									borderRadiusSize="sm"
									paddingSize="md"
								>
									<SetlistSummary
										teamSongIds={selectedSongIds}
									/>
								</ModalSectionCard>
							);
						}

						if (selectedIndex === 1) {
							return !generateSettings.bibleReference ? (
								<ModalSectionCard
									borderRadiusSize="sm"
									paddingSize="md"
								>
									<ModalSectionTitle centered>
										{postSermonSongName ? (
											<>
												Analyze {postSermonSongName}
												<br />
												with your sermon passage
											</>
										) : (
											'Add your passage to get sermon analysis'
										)}
									</ModalSectionTitle>

									<AsaphButton
										color="grey"
										label="Add Passage"
										className="mt-sm"
										fullWidth
										onClick={() => {
											fireOpenAddPassage();
											setIsAddPassageOpen(true);
										}}
									/>
								</ModalSectionCard>
							) : (
								<ModalSectionCard
									borderRadiusSize="sm"
									paddingSize="0"
								>
									<SongAnalysis
										bibleReference={
											generateSettings.bibleReference
										}
										teamSongId={postSermonSongId}
									/>
								</ModalSectionCard>
							);
						}

						if (selectedIndex === 2) {
							return (
								<SetlistAnalysis
									variant="list"
									isLoading={isLoading}
									biblePassageAnalysis={biblePassageAnalysis}
									reference={generateSettings.bibleReference}
									teamSongIds={selectedSongIds}
									postSermonSongPosition={
										generateSettings.postSermonSongPosition
									}
								/>
							);
						}
					}}
				/>
			</BottomActionLayout>

			{isAddPassageOpen && (
				<AddPassageBottomCard
					onClose={() => {
						setIsAddPassageOpen(false);
					}}
					onSave={(bibleReference) => {
						setGenerateSettings((state) => ({
							...state,
							bibleReference,
						}));
						setIsAddPassageOpen(false);
					}}
				/>
			)}

			{isSongsPanelOpen &&
				(() => {
					const postPos = generateSettings.postSermonSongPosition;
					const preSlots =
						typeof postPos === 'number'
							? postPos - 1
							: generateSettings.totalSongPositions;
					const postIds =
						typeof postPos === 'number'
							? selectedSongIds.slice(postPos - 1)
							: [];
					const currentPreSongCount = selectedSongIds
						.slice(0, preSlots)
						.filter((id) => !id.startsWith('song-')).length;
					const currentPostSongCount = postIds.filter(
						(id) => !id.startsWith('song-'),
					).length;
					const initialPre =
						typeof postPos === 'number'
							? postPos - 1
							: generateSettings.totalSongPositions;
					const initialPost =
						typeof postPos === 'number'
							? generateSettings.totalSongPositions -
							  (postPos - 1)
							: 0;
					return (
						<SongsChipPanel
							initialPre={initialPre}
							initialPost={initialPost}
							currentPreSongCount={currentPreSongCount}
							currentPostSongCount={currentPostSongCount}
							onSave={({
								totalSongPositions,
								postSermonSongPosition,
							}) => {
								setSelectedSongIds((ids) => {
									const newIds = [...ids];
									if (newIds.length > totalSongPositions)
										return newIds.slice(
											0,
											totalSongPositions,
										);
									while (newIds.length < totalSongPositions)
										newIds.push(
											`song-${newIds.length + 1}`,
										);
									return newIds;
								});
								setGenerateSettings((s) => ({
									...s,
									totalSongPositions,
									postSermonSongPosition,
								}));
								setIsSongsPanelOpen(false);
							}}
							onClose={() => setIsSongsPanelOpen(false)}
						/>
					);
				})()}

			{isPassagePanelOpen && (
				<PassageChipPanel
					initialBibleReference={generateSettings.bibleReference}
					onSave={(bibleReference) => {
						setGenerateSettings((s) => ({ ...s, bibleReference }));
						setIsPassagePanelOpen(false);
					}}
					onClose={() => setIsPassagePanelOpen(false)}
				/>
			)}
		</>
	);
};

export default StepSeven;
